import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  AtSign,
  Copy,
  ExternalLink,
  Globe,
  Info,
  MessageCircle,
  TriangleAlert,
} from 'lucide-react'
import { useMemo } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { EmptyState, Page, StatCard } from '@/components/shell'
import { HistoryTimeline } from '@/features/history-timeline'
import { TokenActions } from '@/features/tokens/token-actions'
import { TokenLedgerPanel } from '@/features/tokens/token-ledger'
import { TokenLifecycle } from '@/features/tokens/token-lifecycle'
import { chartTickFont, useChartTheme } from '@/lib/chart-theme'
import {
  api,
  type HoldingRow,
  type Paginated,
  type PricePoint,
  type TokenDetail,
  type TokenTradeStats,
  type TradeRow,
} from '@/lib/api'
import {
  chartTimestamp,
  explorerAddressUrl,
  explorerTxUrl,
  formatChartNumber,
  formatDateTime,
  formatUnits,
  hideCategoryLabel,
  isUnknownQuote,
  LAUNCH_TOKEN_DECIMALS,
  priceChartValue,
  progressPercent,
  quoteLabel,
  shortAddress,
} from '@/lib/format'

export const Route = createFileRoute('/dashboard/tokens/$address')({
  component: TokenDetailPage,
})

function TokenDetailPage() {
  const { address } = Route.useParams()

  // 同 dashboard/index.tsx：图表配色不跟 CSS 变量走，按主题取字面量
  const {
    colors: chartColors,
    areaFill,
    tooltipStyle: chartTooltipStyle,
    tooltipLabelStyle: chartTooltipLabelStyle,
  } = useChartTheme()

  const { data: token, isLoading } = useQuery({
    queryKey: ['token', address],
    queryFn: () => api.get<TokenDetail>(`/tokens/${address}`),
  })
  const { data: stats } = useQuery({
    queryKey: ['token', address, 'stats'],
    queryFn: () => api.get<TokenTradeStats>(`/tokens/${address}/stats`),
  })
  const { data: series } = useQuery({
    queryKey: ['token', address, 'price-series'],
    queryFn: () => api.get<{ data: PricePoint[] }>(`/tokens/${address}/price-series`),
  })
  const { data: holders } = useQuery({
    queryKey: ['token', address, 'holders'],
    queryFn: () => api.get<{ data: HoldingRow[] }>(`/tokens/${address}/holders`),
  })
  const { data: trades } = useQuery({
    queryKey: ['token', address, 'trades'],
    queryFn: () =>
      api.get<Paginated<TradeRow>>('/trades', { tokenAddress: address, page: 1, perPage: 20 }),
  })

  /*
   * 走势数据在渲染前先过一遍换算，换不出来的点直接丢掉并计数。
   *
   * ★ 丢点必须让人看见。未知计价币（quoteDecimals 为 null）会让每个点都换算失败，
   *   而 recharts 拿到空数组或 NaN 都**不报错**——前者画一张空白坐标系，
   *   后者把折线画断、Y 轴刻度直接印成 "NaN"。两种都像"这个币没成交"，
   *   但真相是"我们不知道它的计价精度"，处置方式完全不同。
   *
   * Hook 必须排在下面那两个早退之前，否则 token 未就绪的那一帧会少调一次 useMemo。
   */
  const priceChart = useMemo(() => {
    const points: Array<{ t: number; price: number }> = []
    let dropped = 0

    for (const point of series?.data ?? []) {
      const t = chartTimestamp(point.blockTime)
      const price = priceChartValue(point.price, point.quoteDecimals)
      if (t === null || price === null) {
        dropped += 1
        continue
      }
      points.push({ t, price })
    }

    return { points, dropped, total: series?.data.length ?? 0 }
  }, [series])

  if (isLoading) {
    return (
      <Page>
        <Skeleton className="h-28 w-full rounded-apple-lg" />
        <Skeleton className="h-64 w-full rounded-apple-lg" />
      </Page>
    )
  }

  if (!token) {
    return (
      <Page>
        <EmptyState icon={Info} title="代币不存在" description="该合约地址没有对应的发行记录。" />
      </Page>
    )
  }

  const pct = progressPercent(token.collected, token.graduation)
  const unknownQuote = isUnknownQuote(token)

  return (
    <Page>
      <Link
        to="/dashboard/tokens"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-mut hover:text-cyan"
      >
        <ArrowLeft className="size-3.5" />
        返回代币列表
      </Link>

      {/* 头部：身份 + 状态 + 操作 */}
      <div className="surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {token.image ? (
              <img
                src={token.image}
                alt=""
                className="size-14 rounded-apple border border-line/60 object-cover"
              />
            ) : (
              <div className="flex size-14 items-center justify-center rounded-apple border border-line/70 bg-void text-xl font-semibold tracking-tight text-mut2">
                {token.symbol.slice(0, 2)}
              </div>
            )}

            <div>
              <div className="flex flex-wrap items-center gap-2">
                {/*
                  ticker 不用 display-title：那一档是给散文式大标题的。
                  代币符号是标识符不是散文，字距不该再收——BABY1515 这种字母数字
                  混排收紧后会黏成一团。这里单独用半粗 + 常规字距。
                */}
                <h1 className="text-[28px] leading-none font-semibold tracking-tight text-txt">
                  {token.symbol}
                </h1>
                {token.hidden && (
                  <Badge className="bg-warn/15 text-warn hover:bg-warn/15">已下架</Badge>
                )}
                {token.hotActive && (
                  <Badge className="bg-rose/15 text-rose hover:bg-rose/15">热门</Badge>
                )}
                {token.graduated && (
                  <Badge className="bg-green/12 text-green hover:bg-green/12">已毕业</Badge>
                )}
              </div>
              <p className="mt-1.5 text-[13.5px] text-mut">{token.name}</p>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <AddressChip label="合约" address={token.tokenAddress} />
                <Link
                  to="/dashboard/wallets"
                  search={{ address: token.creator }}
                  className="font-mono text-[11.5px] text-mut hover:text-cyan"
                >
                  发行人 {shortAddress(token.creator)}
                </Link>
                {token.website && <SocialLink icon={Globe} href={token.website} label="官网" />}
                {token.twitter && <SocialLink icon={AtSign} href={token.twitter} label="Twitter" />}
                {token.telegram && <SocialLink icon={MessageCircle} href={token.telegram} label="Telegram" />}
              </div>
            </div>
          </div>

          <TokenActions token={token} />
        </div>

        {token.description && (
          <p className="mt-5 max-w-3xl border-t border-line/40 pt-4 text-[13px] leading-relaxed text-mut">
            {token.description}
          </p>
        )}

        {token.hidden && token.hiddenReason && (
          <div className="mt-5 rounded-apple border border-warn/30 bg-warn/[0.06] p-4">
            <div className="section-label text-warn">下架记录</div>
            <p className="mt-1.5 text-[13px] text-txt">
              {hideCategoryLabel(token.hiddenCategory)} ·{' '}
              {token.hiddenReason}
            </p>
            <p className="mt-1 font-mono text-[11px] text-mut2">
              {token.hiddenBy} 于 {formatDateTime(token.hiddenAt)}
            </p>
          </div>
        )}
      </div>

      {/*
        精度查不到时，本页所有金额都会显示成破折号。不解释一句的话，运营会以为是
        接口坏了——真实原因是 ignix 运行时参数里没有这个计价币，而那要去主站侧处理。
      */}
      {unknownQuote && (
        <div className="flex items-start gap-2.5 rounded-apple border border-warn/30 bg-warn/[0.06] px-4 py-3">
          <TriangleAlert className="mt-px size-4 shrink-0 text-warn" />
          <div className="text-[12.5px] leading-relaxed text-txt">
            该代币的计价币{' '}
            <a
              href={explorerAddressUrl(token.quote)}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-warn hover:underline"
            >
              {shortAddress(token.quote, 10, 8)}
            </a>{' '}
            不在 ignix 运行时参数的已知资产里，<b className="font-medium">精度未知</b>。
            <p className="mt-1 text-[11.5px] text-mut2">
              本页所有金额与走势因此一律显示为「—」，而不是按 18 位猜一个数字——
              猜错的量级差在运营决策上比缺数据更危险。
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="surface p-5">
          <div className="section-label">募资进度</div>
          <div className="stat-num mt-3 text-[30px] text-txt">
            {token.graduated ? '已毕业' : `${pct.toFixed(1)}%`}
          </div>
          {!token.graduated && <Progress value={pct} className="mt-3 h-1.5" />}
          <div className="tnum mt-2 font-mono text-[11px] text-mut2">
            {unknownQuote ? (
              <span className="text-warn">计价币精度未知，金额不换算</span>
            ) : (
              <>
                {formatUnits(token.collected, token.quoteDecimals, 2)} /{' '}
                {formatUnits(token.graduation, token.quoteDecimals, 2)} {quoteLabel(token)}
              </>
            )}
          </div>
        </div>

        <StatCard
          label="成交笔数"
          value={stats ? stats.buyCount + stats.sellCount : '—'}
          hint={stats ? `买 ${stats.buyCount} · 卖 ${stats.sellCount}` : undefined}
          tone="cyan"
        />
        <StatCard
          label="参与地址"
          value={stats?.traderCount ?? '—'}
          hint={stats?.firstTradeAt ? `首笔 ${formatDateTime(stats.firstTradeAt)}` : undefined}
        />
        <StatCard
          label="累计成交额"
          value={
            stats
              ? `${formatUnits(stats.totalVolume, stats.quoteDecimals, 2)} ${
                  stats.quote ? quoteLabel({ quoteSymbol: stats.quoteSymbol, quote: stats.quote }) : ''
                }`.trim()
              : '—'
          }
          hint={stats?.lastTradeAt ? `最近 ${formatDateTime(stats.lastTradeAt)}` : undefined}
        />
      </div>

      <TokenLifecycle token={token} />

      {/* 价格走势 */}
      <div className="surface p-6">
        <div className="mb-4">
          <h2 className="text-[15px] font-medium tracking-tight text-txt">成交价走势</h2>
          <p className="mt-1 text-[12px] text-mut2">
            按成交先后排列，不做等间隔重采样——发射期成交稀疏，强行分桶会造出大量空档
          </p>
        </div>

        {!series ? (
          <Skeleton className="h-[220px] w-full rounded-apple" />
        ) : priceChart.total === 0 ? (
          <EmptyState title="尚无成交" description="该代币还没有产生任何交易记录。" />
        ) : priceChart.points.length === 0 ? (
          /*
            有成交但一个点都画不出来，只可能是精度换算不出来。
            这里必须显式说明——否则和「没有成交」在界面上长得一模一样。
          */
          <EmptyState
            icon={TriangleAlert}
            title="成交价无法换算"
            description={`${priceChart.total} 笔成交都缺少可用的计价币精度，走势图不做绘制。`}
          />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={priceChart.points} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
                <defs>
                  <linearGradient id="gPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors.cyan} stopOpacity={areaFill.primary} />
                    <stop offset="100%" stopColor={chartColors.cyan} stopOpacity={0} />
                  </linearGradient>
                </defs>
                {/*
                  X 轴隐藏但**必须存在并绑定 t**。缺了它，recharts 的类目轴退回下标，
                  tooltip 的 label 就是 0/1/2……，`new Date(0)` 一律格式化成 1970-01-01。
                  仍然是类目轴而非数值轴：等间隔排列是刻意的，见上面那段说明。
                */}
                <XAxis dataKey="t" hide />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: chartColors.tick, fontSize: 11, fontFamily: chartTickFont }}
                  width={62}
                  tickFormatter={(v) => formatChartNumber(v, 3)}
                />
                <Tooltip
                  cursor={{ stroke: chartColors.cursor }}
                  contentStyle={chartTooltipStyle}
                  labelStyle={chartTooltipLabelStyle}
                  // formatDateTime 自己就会把 Invalid Date 兜成破折号，不会印出 "NaN"
                  labelFormatter={(t) => formatDateTime(new Date(Number(t)))}
                  formatter={(v) => [formatChartNumber(v, 6), '成交价']}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={chartColors.cyan}
                  strokeWidth={2}
                  fill="url(#gPrice)"
                />
              </AreaChart>
            </ResponsiveContainer>

            {priceChart.dropped > 0 && (
              <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-warn">
                <TriangleAlert className="size-3.5 shrink-0" />
                有 {priceChart.dropped} 笔成交因精度或时间戳缺失未能入图（共 {priceChart.total} 笔）。
              </p>
            )}
          </>
        )}
      </div>

      {/* 分场馆成交：内盘与毕业后的池子口径不同，合在一起看不出问题出在哪一侧 */}
      {stats && stats.venues.length > 0 && (
        <div className="surface overflow-hidden">
          <div className="border-b border-line/50 px-6 py-4">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">分场馆成交</h2>
            <p className="mt-1 text-[12px] text-mut2">
              curve 是内盘曲线（金额为用户实付毛额），v4 / v2 是毕业后的池子（税在 hook 的 beforeSwap
              就抽走了，不进事件）。两个口径不可直接相加——上方「累计成交额」是各场馆的合计，
              跨口径，只能当量级参考，对账要看这里的分场馆数。
            </p>
          </div>
          <div className="grid gap-px bg-line/35 sm:grid-cols-2 lg:grid-cols-3">
            {stats.venues.map((venue) => (
              <div key={venue.venue} className="bg-void px-5 py-4">
                <div className="flex items-center justify-between">
                  <Badge className="h-5 bg-cyan/10 px-1.5 font-mono text-[10px] uppercase text-cyan hover:bg-cyan/10">
                    {venue.venue}
                  </Badge>
                  <span className="tnum font-mono text-[11px]">
                    <span className="text-up">{venue.buyCount}</span>
                    <span className="text-dim"> / </span>
                    <span className="text-dn">{venue.sellCount}</span>
                  </span>
                </div>
                <div className="stat-num mt-2.5 text-[19px] text-txt">
                  {formatUnits(venue.totalVolume, stats.quoteDecimals, 3)}
                  {stats.quote && (
                    <span className="ml-1.5 text-[11.5px] font-normal text-mut2">
                      {quoteLabel({ quoteSymbol: stats.quoteSymbol, quote: stats.quote })}
                    </span>
                  )}
                </div>
                <div className="tnum mt-1.5 font-mono text-[10.5px] text-mut2">
                  平台费 {formatUnits(venue.platformFee, stats.quoteDecimals, 4)} · 项目费{' '}
                  {formatUnits(venue.projectFee, stats.quoteDecimals, 4)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3.5 lg:grid-cols-2">
        {/* Transfer 事件索引维护的真实持仓 */}
        <div className="surface overflow-hidden">
          <div className="p-6 pb-3">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">链上持仓</h2>
            <p className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-relaxed text-mut2">
              <Info className="mt-px size-3.5 shrink-0" />
              来自 ERC-20 Transfer 事件索引，包含转账与毕业后的流动性变动。
            </p>
          </div>

          {!holders ? (
            <div className="space-y-2 p-6 pt-0">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded-apple-sm" />
              ))}
            </div>
          ) : holders.data.length === 0 ? (
            <EmptyState title="尚无成交" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="section-label pl-6">地址</TableHead>
                  <TableHead className="section-label text-right">余额</TableHead>
                  <TableHead className="section-label pr-6 text-right">更新区块</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holders.data.slice(0, 10).map((h) => (
                  <TableRow key={h.holder} className="row-hover border-line/40">
                    <TableCell className="pl-6">
                      <a
                        href={explorerAddressUrl(h.holder)}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-[11.5px] text-mut hover:text-cyan"
                      >
                        {shortAddress(h.holder)}
                      </a>
                    </TableCell>
                    <TableCell className="tnum text-right font-mono text-[11.5px] text-txt">
                      {/* 这里换算的是**发射代币自己**的余额，固定 18 位；别顺手改成 quoteDecimals */}
                      {formatUnits(h.amount, LAUNCH_TOKEN_DECIMALS, 4)}
                    </TableCell>
                    <TableCell className="tnum pr-6 text-right font-mono text-[11.5px] text-mut2">
                      {h.blockNumber}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 最近成交 */}
        <div className="surface overflow-hidden">
          <div className="flex items-center justify-between p-6 pb-3">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">最近成交</h2>
            <Link
              to="/dashboard/trades"
              search={{ tokenAddress: address }}
              className="text-[12px] text-cyan hover:underline"
            >
              全部记录
            </Link>
          </div>

          {!trades ? (
            <div className="space-y-2 p-6 pt-0">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded-apple-sm" />
              ))}
            </div>
          ) : trades.data.length === 0 ? (
            <EmptyState title="尚无成交" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="section-label pl-6">方向</TableHead>
                  <TableHead className="section-label">地址</TableHead>
                  <TableHead className="section-label text-right">金额</TableHead>
                  <TableHead className="section-label pr-6 text-right">时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.data.slice(0, 10).map((t) => (
                  <TableRow key={t.id} className="row-hover border-line/40">
                    <TableCell className="pl-6">
                      <span
                        className={`font-mono text-[11px] ${t.side === 'buy' ? 'text-up' : 'text-dn'}`}
                      >
                        {t.side === 'buy' ? '买入' : '卖出'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[11.5px] text-mut">
                      {shortAddress(t.trader, 6, 4)}
                    </TableCell>
                    <TableCell className="tnum text-right font-mono text-[11.5px]">
                      {formatUnits(t.quoteAmount, t.quoteDecimals, 3)} {quoteLabel(t)}
                    </TableCell>
                    <TableCell className="pr-6 text-right">
                      <a
                        href={explorerTxUrl(t.txHash)}
                        target="_blank"
                        rel="noreferrer"
                        className="tnum font-mono text-[11px] text-mut2 hover:text-cyan"
                      >
                        {formatDateTime(t.blockTime)}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <TokenLedgerPanel token={token} />

      {/* 处置历史：谁在什么时候因为什么处置过这个币 */}
      <div className="surface p-6">
        <h2 className="mb-4 text-[15px] font-medium tracking-tight text-txt">处置历史</h2>
        <HistoryTimeline scope="tokens" address={address} />
      </div>
    </Page>
  )
}

function AddressChip({ label, address }: { label: string; address: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="font-mono text-[11.5px] text-mut2">{label}</span>
      <button
        className="pressable font-mono text-[11.5px] text-mut hover:text-cyan"
        onClick={() => {
          navigator.clipboard.writeText(address)
          toast.success('地址已复制')
        }}
        title={address}
      >
        {shortAddress(address)}
        <Copy className="ml-1 inline size-3" />
      </button>
      <a
        href={explorerAddressUrl(address)}
        target="_blank"
        rel="noreferrer"
        title="在区块浏览器中查看"
        className="text-mut2 hover:text-cyan"
      >
        <ExternalLink className="size-3" />
      </a>
    </span>
  )
}

function SocialLink({
  icon: Icon,
  href,
  label,
}: {
  icon: typeof Globe
  href: string
  label: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1 text-[11.5px] text-mut hover:text-cyan"
    >
      <Icon className="size-3.5" />
      {label}
    </a>
  )
}
